import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { Person } from '../../models/campaign.model';

/** Reusable live people search + add/remove chips for inclusions/exclusions. */
@Component({
  selector: 'app-people-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './people-picker.component.html',
  styleUrls: ['./people-picker.component.css'],
})
export class PeoplePickerComponent {
  @Input() label = 'Inclusions';
  @Input() current: string[] = [];
  /** 'include' warns when a candidate is already in the audience; 'exclude' warns when they aren't. */
  @Input() mode: 'include' | 'exclude' = 'include';
  /** Normalized (lowercase) emails to check candidates against. */
  @Input() checkAgainstEmails: Set<string> = new Set();

  query = '';
  candidates: Person[] = [];
  selectedEmails: Set<string> = new Set();
  shown = 10;
  private readonly pageSize = 10;
  searching = false;

  constructor(private api: ApiService, private cdr: ChangeDetectorRef) { }

  private norm(email: string): string {
    return (email || '').trim().toLowerCase();
  }

  /** Warning shown for a given email based on whether they're already part of the current audience. */
  membershipWarning(email: string): string | null {
    const inAudience = this.checkAgainstEmails.has(this.norm(email));
    if (this.mode === 'include' && inAudience) {
      return 'Already part of the current audience';
    }
    if (this.mode === 'exclude' && !inAudience) {
      return 'Not part of current audience, exclusion not needed';
    }
    return null;
  }

  search(): void {
    if (!this.query.trim()) {
      return;
    }
    this.searching = true;
    this.api.searchPeople(this.query, 500).subscribe({
      next: (people) => {
        this.candidates = people;
        this.selectedEmails.clear();
        this.shown = this.pageSize;
        this.searching = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.candidates = [];
        this.searching = false;
        this.cdr.detectChanges();
      },
    });
  }

  get visibleCandidates(): Person[] {
    return this.candidates.slice(0, this.shown);
  }

  showMore(): void {
    this.shown = Math.min(this.shown + this.pageSize, this.candidates.length);
  }

  get remainingCount(): number {
    return Math.min(this.pageSize, this.candidates.length - this.visibleCandidates.length);
  }

  toggleSelected(email: string): void {
    if (this.selectedEmails.has(email)) {
      this.selectedEmails.delete(email);
    } else {
      this.selectedEmails.add(email);
    }
  }

  isSelected(email: string): boolean {
    return this.selectedEmails.has(email);
  }

  addSelected(): void {
    for (const email of this.selectedEmails) {
      if (!this.current.includes(email)) {
        this.current.push(email);
      }
    }
    this.selectedEmails.clear();
    this.candidates = [];
    this.query = '';
  }

  removeCurrent(email: string): void {
    const idx = this.current.indexOf(email);
    if (idx >= 0) {
      this.current.splice(idx, 1);
    }
  }
}
